/// <amd-module name="TFS/CreateProject/Components/CreateProjectPanel" />
import * as React from "react";
import { ActionsHub } from "TFS/CreateProject/ActionsHub";
import { CreateProjectComponent } from "TFS/CreateProject/Components/CreateProjectComponent";
import { Constants } from "TFS/CreateProject/Constants";
import { StatusMessageType, StatusValueType, INewProjectParameters } from "TFS/CreateProject/Contracts";
import { ICreateProjectComponentState } from "TFS/CreateProject/Models";
import * as CreateProjectResources from "TFS/CreateProject/Resources";
import { StoresHub } from "TFS/CreateProject/StoresHub";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { ContentSize } from "VSSUI/Callout";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

interface ICreateProjectPanelProps {
    /**
     * Flag to show/hide panel
     */
    showPanel: boolean;
    /**
     * Handler for panel dismiss
     */
    onPanelDismiss: () => void;
    /**
     * Optional handler for create project complete
     */
    onCreateProjectComplete?: (newProject: string) => void;
    /**
     * Source module from where create project panel is being invoked. Used for telemetry.
     */
    invokingSource: string;
    /**
     * Optional: default version control type
     */
    versionControlType?: string;
    /**
     * Optional: default process template
     */
    processTemplate?: string;
    /**
     * Optional: project visibility option (i.e., Everyone/TeamMembers/EveryoneInTenant)
     */
    projectVisibility?: string;
    /**
     * Optional: renders light weight panel view with only project name and visibility options.
     */
    isLightWeightView?: boolean;
    /**
     * Optional: acquisition id which will be added in CI events to trace back.
     */
    acquisitionId?: boolean;
    /**
     * Optional: List of ids of the features to enable when creating the project (i.e., "ms.vss-build.pipelines, ms.vss-work.agile", etc.). All features are enabled if omitted.
     */
    defaultFeatureIds?: string[];
    /**
     * Optional: Avoid redirect the current page to the newly create project details page
     */
    skipRedirectToDetails?: boolean;
}

const scenarioName = "CreateProjectPanel.Invoked";

class CreateProjectPanel extends VssComponent<ICreateProjectPanelProps, ICreateProjectComponentState> {
    public static componentType = "createProjectPanel";
    private _performanceService: IVssPerformanceService;
    private _actionsHub: ActionsHub;
    private _storesHub: StoresHub;

    constructor(props: ICreateProjectPanelProps, context: IVssComponentContext) {
        super(props, context);

        this._performanceService = context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this._performanceService.startScenario(scenarioName, false);

        this._actionsHub = new ActionsHub();
        this._storesHub = new StoresHub(
            this._actionsHub,
            props.invokingSource,
            props.versionControlType,
            props.processTemplate,
            props.projectVisibility,
            props.defaultFeatureIds
        );

        this.state = this._storesHub.store.state;
    }

    public render(): JSX.Element | null {
        const statusMessage = this.state.statusMessage.message;
        const waitingForProjectCreation = this.state.statusMessage.statusMessageType === StatusMessageType.Waiting;

        return (
            this.props.showPanel ? (
                isFeatureFlagEnabled(this.context.pageContext, Constants.NewCreateProjectPanelErrorMessageCard) ? (
                    <Panel
                        className="create-project-panel"
                        onDismiss={this._onDismissButtonClick}
                        titleProps={{ text: CreateProjectResources.CreateProjectFormHeader }}
                        size={ContentSize.Large}
                        overlayContent={
                            waitingForProjectCreation ? <Spinner label={statusMessage} /> : null
                        }
                    >
                        <CreateProjectComponent
                            className="panel-view flex-grow"
                            onCancel={this.props.onPanelDismiss}
                            onScenarioComplete={this._onScenarioComplete}
                            invokingSource={this.props.invokingSource}
                            processTemplate={this.props.processTemplate}
                            projectVisibility={this.props.projectVisibility}
                            versionControlType={this.props.versionControlType}
                            acquisitionId={this.props.acquisitionId}
                            isLightWeightView={this.props.isLightWeightView}
                            defaultFeatureIds={this.props.defaultFeatureIds}
                            actionsHub={this._actionsHub}
                            storesHub={this._storesHub}
                            skipRedirectToDetails={this.props.skipRedirectToDetails}
                            onCreateProjectComplete={this.props.onCreateProjectComplete}
                        />
                    </Panel>
                ) : (
                    <Panel
                        className="create-project-panel"
                        onDismiss={this._onDismissButtonClick}
                        titleProps={{ text: CreateProjectResources.CreateProjectFormHeader }}
                        size={ContentSize.Large}
                    >
                        <CreateProjectComponent
                            className="panel-view flex-grow"
                            onCancel={this.props.onPanelDismiss}
                            onScenarioComplete={this._onScenarioComplete}
                            invokingSource={this.props.invokingSource}
                            processTemplate={this.props.processTemplate}
                            projectVisibility={this.props.projectVisibility}
                            versionControlType={this.props.versionControlType}
                            acquisitionId={this.props.acquisitionId}
                            isLightWeightView={this.props.isLightWeightView}
                            defaultFeatureIds={this.props.defaultFeatureIds}
                            actionsHub={this._actionsHub}
                            storesHub={this._storesHub}
                            skipRedirectToDetails={this.props.skipRedirectToDetails}
                            onCreateProjectComplete={this.props.onCreateProjectComplete}
                        />
                    </Panel>
                )
            ) : null
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._storesHub.store.addChangedListener(this._onStoreChanged);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this._storesHub.store.removeChangedListener(this._onStoreChanged);
    }

    /**
     * Click handler for the Dismiss button
     */
    private _onDismissButtonClick = (): void => {
        this._cancelCreateProject("DismissButton");
    };

    private _cancelCreateProject = (ctaSource: string): void => {
        const creationStatus = this.state.creationStatus.value;
        const waitingForProjectCreation = creationStatus === StatusValueType.InProgress || creationStatus === StatusValueType.Success; // Need StatusValueType.Success as well because we show spinner while navigating once project created

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(Constants.Area, "CreateProjectPanel.CancelButtonClicked", {
            source: this.state.source,
            ctaSource: ctaSource,
            waitingForProjectCreation: waitingForProjectCreation
        });

        if (this.props.onPanelDismiss) {
            this.props.onPanelDismiss();
        }
    };

    private _onStoreChanged = (): void => {
        this.setState(this._storesHub.store.state);
    };

    private _onScenarioComplete = (status: string) => {
        this._performanceService.endScenario(Constants.Area, scenarioName, false, undefined, { status: status });

        const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent(Constants.Area, "CreateProjectPanel.Invoked", {
            source: this.props.invokingSource,
            pageDataStatus: status
        });
    };
}

VssComponent.register(CreateProjectPanel.componentType, CreateProjectPanel);
